import { useQuery } from '@tanstack/react-query';
import type { StaticVideoChannel } from '@brimo/shared';
import { api } from '../../lib/api';
import type { ToolKey } from '../../lib/session';

/**
 * The organization's user directory (`GET /users`, docs/dev/api-users.md „Directory"): open to every signed-in
 * user, sorted by last and first name. It replaces the lists earlier milestones composed from other contracts
 * (M3 `useKnownUsers`, the event-type member candidates, D-70). Query root `['users']` (realtime `user.updated`).
 */
export interface UserDirectoryItem {
  id: string;
  /** „Anna Brandl" */
  name: string;
  /** „A. Brandl" */
  shortName: string;
  initials: string;
  roleKey: string;
  roleLabel: string;
  jobTitle: string | null;
  avatarUrl: string | null;
  active: boolean;
  teamId: string | null;
  managerId: string | null;
  /** External company of the membership (0037) — „extern · <Firma>", a label only. */
  externalCompany: string | null;
  /** Static meeting channels the user has a personal link for in the profile — never the URLs themselves. */
  videoLinks: StaticVideoChannel[];
}

export interface UserDirectoryFilter {
  q?: string | undefined;
  /** Role keys, e.g. `['advisor', 'sales_lead']`. */
  roles?: readonly string[] | undefined;
  tool?: ToolKey | undefined;
  includeInactive?: boolean | undefined;
}

export const usersQueryRoot = ['users'] as const;

function directoryPath(filter: UserDirectoryFilter): string {
  const params = new URLSearchParams();
  if (filter.q) params.set('q', filter.q);
  if (filter.roles && filter.roles.length > 0) params.set('role', filter.roles.join(','));
  if (filter.tool) params.set('tool', filter.tool);
  if (filter.includeInactive) params.set('includeInactive', 'true');
  const query = params.toString();
  return query ? `/users?${query}` : '/users';
}

export function useUserDirectory(filter: UserDirectoryFilter = {}, enabled = true) {
  const path = directoryPath(filter);
  return useQuery({
    queryKey: [...usersQueryRoot, 'directory', path],
    queryFn: () => api<{ items: UserDirectoryItem[] }>(path),
    select: (data) => data.items,
    staleTime: 60_000,
    enabled,
  });
}
